/**
 * Denomination-aware amount formatting for display and screen readers.
 *
 * Supports BIP-177 (unicode bitcoin symbol), sats, and BTC display formats.
 * All functions are pure — no side effects, no network calls.
 *
 * Tier: All tiers (core formatting infrastructure).
 *
 * @module lib/amount-formatter
 */

import type { Denomination } from '../types/denomination';

/** Satoshis per bitcoin (10^8). */
const SATS_PER_BTC = 100_000_000;

/** Unicode bitcoin sign (U+20BF). */
const BITCOIN_SIGN = '\u20BF';

/** Intl formatter for locale-aware integer grouping. */
const integerFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

/**
 * Number-to-words mapping for screen reader natural language output.
 * Covers numbers up to the trillions (enough for max bitcoin supply in sats).
 */
const ONES = [
  '',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
];

const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

const SCALES = ['', 'thousand', 'million', 'billion', 'trillion'];

/**
 * Convert a non-negative integer to English words.
 *
 * @param n - Non-negative integer
 * @returns English word representation (e.g., 12500 -> "twelve thousand five hundred")
 */
function numberToWords(n: number): string {
  if (n === 0) return 'zero';

  const groups: string[] = [];
  let remaining = n;
  let scaleIndex = 0;

  while (remaining > 0) {
    const chunk = remaining % 1000;
    if (chunk !== 0) {
      const chunkWords = chunkToWords(chunk);
      const scale = SCALES[scaleIndex];
      groups.unshift(scale ? `${chunkWords} ${scale}` : chunkWords);
    }
    remaining = Math.floor(remaining / 1000);
    scaleIndex++;
  }

  return groups.join(' ');
}

/**
 * Convert a 1-999 number chunk to English words.
 *
 * @param n - Integer between 1 and 999
 * @returns English words for the chunk
 */
function chunkToWords(n: number): string {
  const parts: string[] = [];
  let remaining = n;

  if (remaining >= 100) {
    parts.push(`${ONES[Math.floor(remaining / 100)] ?? ''} hundred`);
    remaining %= 100;
  }

  if (remaining >= 20) {
    const ten = TENS[Math.floor(remaining / 10)] ?? '';
    const one = ONES[remaining % 10] ?? '';
    parts.push(one ? `${ten}-${one}` : ten);
  } else if (remaining > 0) {
    parts.push(ONES[remaining] ?? '');
  }

  return parts.join(' ');
}

/**
 * Format a satoshi amount for visual display.
 *
 * @param sats - Amount in satoshis (non-negative integer)
 * @param denomination - Display format to use
 * @returns Formatted string for display (e.g., "₿ 12,500", "12,500 sats", "0.00012500 BTC")
 */
export function formatAmount(sats: number, denomination: Denomination): string {
  switch (denomination) {
    case 'bip177':
      return `${BITCOIN_SIGN} ${integerFormatter.format(sats)}`;

    case 'sats':
      return `${integerFormatter.format(sats)} ${sats === 1 ? 'sat' : 'sats'}`;

    case 'btc': {
      const btcValue = (sats / SATS_PER_BTC).toFixed(8);
      return `${btcValue} BTC`;
    }
  }
}

/**
 * Format a satoshi amount for screen reader announcement.
 *
 * Uses natural language — no symbols, spelled-out numbers where appropriate.
 * BIP-177 and sats both announce in sats. BTC announces in bitcoins.
 *
 * @param sats - Amount in satoshis (non-negative integer)
 * @param denomination - Display format context
 * @returns Natural language string for aria-label / screen reader (e.g., "twelve thousand five hundred sats")
 */
export function formatAmountForScreenReader(sats: number, denomination: Denomination): string {
  switch (denomination) {
    case 'bip177':
      return `${numberToWords(sats)} ${sats === 1 ? 'sat' : 'sats'}`;

    case 'sats':
      return `${integerFormatter.format(sats)} ${sats === 1 ? 'sat' : 'sats'}`;

    case 'btc': {
      const btcValue = sats / SATS_PER_BTC;
      if (btcValue === 1) return 'one bitcoin';
      return `${(sats / SATS_PER_BTC).toFixed(8)} bitcoins`;
    }
  }
}

/**
 * Format a satoshi amount as a fiat currency string.
 *
 * Uses Intl.NumberFormat for locale-aware currency display.
 *
 * @param sats - Amount in satoshis
 * @param btcRate - BTC price in the target currency
 * @param currency - ISO 4217 currency code (e.g., 'USD', 'EUR')
 * @returns Formatted fiat string (e.g., "$65.00", "€60.00")
 */
export function formatFiat(sats: number, btcRate: number, currency: string): string {
  const fiatValue = satsToFiat(sats, btcRate);
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(fiatValue);
}

/**
 * Convert satoshis to a fiat amount using the given BTC rate.
 *
 * @param sats - Amount in satoshis
 * @param btcRate - BTC price in the target currency (e.g., 65000 for $65,000/BTC)
 * @returns Fiat amount as a number (e.g., 65 for 100,000 sats at $65,000/BTC)
 */
export function satsToFiat(sats: number, btcRate: number): number {
  return sats * (btcRate / SATS_PER_BTC);
}
